import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, throwError, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth-service';
import { LookupListResponse } from '../../pages/admin-dashboard/admin-route-instance-management/route-instances.model';
import {
  GetReturnRequestsQueryParams,
  ReturnRequestListResponse,
  ApiErrorResponse,
  LookupVTO,
} from '../../pages/admin-dashboard/return-of-requests-list/return-of-requests-list.model';

@Injectable({
  providedIn: 'root',
})
export class ReturnRequestsService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private readonly API = environment.pharmacyMgtApiUrl;
  private readonly ENDPOINT = `${this.API}/orders/return`;
  private readonly LOOKUP_ENDPOINT = `${this.API}/lookups`;

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private buildParams(queryParams: GetReturnRequestsQueryParams): HttpParams {
    let params = new HttpParams();

    if (queryParams.orderId !== undefined && queryParams.orderId !== null) {
      params = params.set('orderId', queryParams.orderId.toString());
    }
    if (queryParams.storeId !== undefined && queryParams.storeId !== null) {
      params = params.set('storeId', queryParams.storeId.toString());
    }
    if (queryParams.requestStatusId !== undefined && queryParams.requestStatusId !== null) {
      params = params.set('requestStatusId', queryParams.requestStatusId.toString());
    }
    if (queryParams.createdById !== undefined && queryParams.createdById !== null) {
      params = params.set('createdById', queryParams.createdById.toString());
    }
    if (queryParams.createdOnFrom) {
      params = params.set('createdOnFrom', queryParams.createdOnFrom);
    }
    if (queryParams.createdOnTo) {
      params = params.set('createdOnTo', queryParams.createdOnTo);
    }
    if (queryParams.lastModifiedOnFrom) {
      params = params.set('lastModifiedOnFrom', queryParams.lastModifiedOnFrom);
    }
    if (queryParams.lastModifiedOnTo) {
      params = params.set('lastModifiedOnTo', queryParams.lastModifiedOnTo);
    }
    if (queryParams.orderBy) {
      params = params.set('orderBy', queryParams.orderBy);
    }
    if (queryParams.orderDir) {
      params = params.set('orderDir', queryParams.orderDir);
    }
    if (queryParams.pageNum !== undefined && queryParams.pageNum !== null) {
      params = params.set('pageNum', queryParams.pageNum.toString());
    }
    if (queryParams.pageSize !== undefined && queryParams.pageSize !== null) {
      params = params.set('pageSize', queryParams.pageSize.toString());
    }

    return params;
  }

  getReturnRequests(queryParams: GetReturnRequestsQueryParams): Observable<ReturnRequestListResponse> {
    const headers = this.getAuthHeaders();
    const params = this.buildParams(queryParams);

    return this.http.get<ReturnRequestListResponse>(this.ENDPOINT, { params, headers }).pipe(
      tap((response) => console.log(`✓ Retrieved ${response.list?.length ?? 0} of ${response.total} return requests`)),
      catchError(this.handleError),
    );
  }

  getStatusesByDomainId(domainId: number): Observable<LookupListResponse<LookupVTO>> {
    const headers = this.getAuthHeaders();
    return this.http.get<LookupListResponse<LookupVTO>>(`${this.API}/lookups/domain/${domainId}`, { headers }).pipe(
      tap((response) => console.log('Statuses Response:', response)),
      catchError(this.handleError),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const apiError: ApiErrorResponse = error.error;
    return throwError(() => apiError ?? error);
  }
}
